import type { IConfig } from '@cian/config/shared';
import type { IHttpApi } from '@cian/http-api/shared';
import type { ILogger } from '@cian/logger/shared';
import { IMessageResponse, EMessageRequestTypes, EMessageResponseTypes, IMessage } from '@cian/push-manager';

import { getSubscriptionData } from '../../shared/utils/localStorage/storage';
import { validateOrigin } from '../../shared/utils/url/validateOrigin';
import { registerWorker } from '../common/registerWorker';
import { registerEndpoint } from '../common/registerEndpoint';

interface IDependencies {
  config: IConfig;
  httpApi: IHttpApi;
  logger: ILogger;
}

export interface IIFrameManagerProps {
  dependencies: IDependencies;
  workerSrc: string;
}

export class IFrameManager {
  public workerSrc: string;
  public dependencies: IDependencies;
  public serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

  private getWorkerSubscription = async (): Promise<PushSubscription | null> => {
    let subscription = null;
    if (this.serviceWorkerRegistration) {
      try {
        subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
        // eslint-disable-next-line no-empty
      } catch {}
    }

    return subscription;
  };

  private checkSubscription = async () => {
    const subscription = await this.getWorkerSubscription();

    return Boolean(subscription) && Boolean(getSubscriptionData());
  };

  private getSubscriptionHandler =
    () =>
    async (messageRequest: IMessage): Promise<IMessageResponse> => {
      const response = {
        id: messageRequest.id,
        type: EMessageResponseTypes.subscriptionStatus,
        value: false,
      };
      try {
        response.value = await this.checkSubscription();
        // eslint-disable-next-line no-empty
      } catch {}

      return response;
    };

  private getMessageHandler = (messageType: EMessageRequestTypes) => {
    switch (messageType) {
      case EMessageRequestTypes.getSubscription: {
        return this.getSubscriptionHandler();
      }
    }
  };

  private postResponse = (response: IMessageResponse, destination: Window, origin: string) => {
    destination.postMessage(JSON.stringify(response), origin);
  };

  private handleMessage = async (event: MessageEvent) => {
    if (event.data) {
      let messageRequest;

      try {
        messageRequest = JSON.parse(event.data);
      } catch (e) {
        return;
      }

      if (!validateOrigin(event.origin)) {
        return;
      }

      const handler = this.getMessageHandler(messageRequest.type);

      if (handler) {
        const response = await handler(messageRequest);
        this.postResponse(response, event.source as Window, event.origin);
      }
    }
  };

  private registerMessageHandler() {
    window.addEventListener('message', this.handleMessage);
  }

  public register = async () => {
    this.serviceWorkerRegistration = await registerWorker(this.dependencies, this.workerSrc);

    if (this.serviceWorkerRegistration) {
      this.registerMessageHandler();
    }
  };

  public ensureSubscription = async () => {
    const { httpApi, config } = this.dependencies;
    const subscription = await this.getWorkerSubscription();
    const subscriptionData = getSubscriptionData();
    const configUserId = config.get<string>('userId') || '';

    if (subscription) {
      if (!subscriptionData || configUserId.toString() !== subscriptionData) {
        await registerEndpoint(httpApi, subscription as PushSubscription);
      }
    }
  };

  public constructor(props: IIFrameManagerProps) {
    this.workerSrc = props.workerSrc;
    this.dependencies = props.dependencies;
  }
}
